import { useState, useCallback, useEffect } from 'react';
import { ReCaptchaV3 } from '@/utils/hooks/RecaptchaV3';
import { IS_CAPTCHA_ENABLED } from '@/constants/allconstants';
interface UseCaptchaOptions {
  onV3Failure?: (error: any) => void;
  onV2Failure?: (error: any) => void;
}

interface CaptchaData {
  captcha_token: string;
  captcha_type: 'V3' | 'V2';
}

export const useCaptcha = (options: UseCaptchaOptions = {}) => {
  const [captchaV3enabled, setCaptchaV3enabled] = useState(true);
  const [captchaV2Token, setCaptchaV2Token] = useState('');
  const executeRecaptchaV3 = ReCaptchaV3();

  // Reset V2 token when V3 is re-enabled
  useEffect(() => {
    if (captchaV3enabled) {
      setCaptchaV2Token('');
    }
  }, [captchaV3enabled]);

  const handleCaptchaError = useCallback((err: any, isV3: boolean) => {
    if (isV3) {
      setCaptchaV3enabled(false);
      if (options.onV3Failure) {
        options.onV3Failure(err);
      }
    } else {
      setCaptchaV2Token('');
      if (options.onV2Failure) {
        options.onV2Failure(err);
      }
    }
  }, [options]);

  const executeCaptcha = useCallback(async (action: string, callback: any) => {
    if (!IS_CAPTCHA_ENABLED) {
      return { isCaptchaEnabled: false };
    }

    try {
      let captchaData: CaptchaData;

      if (captchaV3enabled) {
        try {
          const reCaptchaToken = await executeRecaptchaV3(action);
          captchaData = {
            captcha_token: reCaptchaToken,
            captcha_type: 'V3'
          };
          callback(captchaData);

          return { 
            isCaptchaEnabled: true,
            captchaData,
            success: true
          };
        } catch (err: any) {
          handleCaptchaError(err, true);

          return { 
            isCaptchaEnabled: true,
            success: false,
            error: err,
            fallbackToV2: true
          };
        }
      } else if (captchaV2Token) {
        captchaData = {
          captcha_token: captchaV2Token,
          captcha_type: 'V2'
        };
        callback(captchaData);
        return { 
          isCaptchaEnabled: true,
          captchaData,
          success: true
        };
      } else {

        // V2 token not yet provided
        return { 
          isCaptchaEnabled: true,
          success: false,
          needsV2Token: true
        };
      }
    } catch (err: any) {
      handleCaptchaError(err, false);

      return { 
        isCaptchaEnabled: true,
        success: false,
        error: err
      };
    }
  }, [captchaV3enabled, captchaV2Token, executeRecaptchaV3, handleCaptchaError]);

  return {
    captchaV3enabled,
    setCaptchaV3enabled,
    captchaV2Token,
    setCaptchaV2Token,
    executeCaptcha,
    isCaptchaEnabled: IS_CAPTCHA_ENABLED,
    isSubmitDisabled: IS_CAPTCHA_ENABLED && !captchaV3enabled && !captchaV2Token,
    isV2TokenRequired: IS_CAPTCHA_ENABLED && !captchaV3enabled && !captchaV2Token
  };
};